import { Pressable, PressableProps } from "react-native";

import { createStyles, sizes } from "../theme";
import { Icon, IconName } from "./icon";
import { Spinner } from "./spinner";
import { TextBody } from "./text";
import { Col } from "./col";

type ButtonVariant = "accent" | "primary";

export type ButtonProps = Omit<PressableProps, "children" | "style"> & {
  children: string;
  compact?: boolean;
  em?: boolean;
  icon?: IconName;
  loading?: boolean;
  variant?: ButtonVariant;
};

export function Button({
  children: title,
  compact = false,
  disabled = false,
  em = false,
  icon,
  loading = false,
  variant: variantName,
  ...rest
}: ButtonProps) {
  const variant = variantName ?? (em ? "accent" : "primary");
  const textColorName = variant === "accent" ? "onAccent" : "onPrimary";

  return (
    <Pressable
      {...rest}
      disabled={disabled || loading}
      hitSlop={sizes["2"]}
      style={({ pressed }) => [
        $.container,
        $[variant],
        compact && $.compact,
        disabled && $.disabled,
        pressed && $active[variant],
      ]}
    >
      <>
        {/* Always render the text, but hide it when loading */}
        {!!title && (
          <TextBody
            em={!compact}
            small
            color={textColorName}
            style={loading ? $.invisible : undefined}
          >
            {title.toUpperCase()}
          </TextBody>
        )}
        {/* Render spinner absolutely over the text when loading */}
        {loading && <Spinner color={textColorName} style={$.spinner} />}
        {!loading && !!icon && (
          <Col ml="1">
            <Icon name={icon} small={compact} color={textColorName} />
          </Col>
        )}
      </>
    </Pressable>
  );
}

const $ = createStyles({
  container: {
    row: 5,
    gap: sizes["1"],
    position: "relative",
    borderRadius: "9999",
    py: "3",
    px: "8",
    // Android shadow
    elevation: 1,
    // iOS shadow
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.3,
    shadowRadius: 1,
  },
  compact: {
    py: "2",
    px: "4",
  },
  accent: {
    backgroundColor: "accent",
  },
  primary: {
    backgroundColor: "surface",
  },
  invisible: {
    opacity: 0,
  },
  spinner: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  disabled: {
    opacity: 0.5,
  },
});

const $active = createStyles({
  accent: {
    backgroundColor: "accentActive",
  },
  primary: {
    backgroundColor: "surfaceActive",
  },
});
